import React, { useEffect, useRef, useState, memo } from "react";
import "../css/MarketBanner.css";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import growwLivePriceService from "../services/growwLivePriceService";
import angelOneWebSocketService, {
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";

const GROW_POLL_SOURCE = "marketBanner";

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

class BaseMarketIndicesAdapter {
  extractList() {
    return [];
  }

  normalizeIndices() {
    return [];
  }
}

class FivePaisaMarketIndicesAdapter extends BaseMarketIndicesAdapter {
  extractList(apiData = {}) {
    return Array.isArray(apiData?.["5paisa"]) ? apiData["5paisa"] : [];
  }

  normalizeIndices(list = []) {
    return list.map((item) => ({
      name: item.Name,
      scripCode: String(item.ScripCode),
      Exch: item.Exch,
      ExchType: item.ExchType,
      growSymbol: null,
      prevClose: null,
    }));
  }
}

class GrowMarketIndicesAdapter extends BaseMarketIndicesAdapter {
  extractList(apiData = {}) {
    return Array.isArray(apiData?.grow) ? apiData.grow : [];
  }

  normalizeIndices(list = []) {
    return list.map((item) => ({
      name: item.name,
      scripCode: item.trading_symbol,
      Exch: item.exchange?.charAt(0) || "N",
      ExchType: "C",
      growSymbol: `${item.exchange}_${item.trading_symbol}`,
      prevClose: Number(item?.ohlc?.close) || null,
    }));
  }
}

class AngelOneMarketIndicesAdapter extends BaseMarketIndicesAdapter {
  extractList(apiData = {}) {
    return Array.isArray(apiData?.angelone) ? apiData.angelone : [];
  }

  normalizeIndices(list = []) {
    return list.map((item) => {
      const exchSeg = String(item?.exch_seg || "NSE").toUpperCase();
      const exch = exchSeg.startsWith("BSE") ? "B" : "N";
      const token = String(item?.token || "").trim();
      const displayName =
        String(item?.name || item?.symbol || "").trim() || token;

      return {
        name: displayName,
        scripCode: token,
        token,
        Exch: exch,
        ExchType: "C",
        ExchSeg: exchSeg,
        exch_seg: exchSeg,
        instrumenttype: String(item?.instrumenttype || ""),
        growSymbol: null,
        prevClose: null,
      };
    });
  }
}

const getMarketIndicesAdapter = (brokerFlag) => {
  const normalized = toPmsBrokerFlag(brokerFlag);
  if (normalized === "grow") return new GrowMarketIndicesAdapter();
  if (normalized === "angelone") return new AngelOneMarketIndicesAdapter();
  return new FivePaisaMarketIndicesAdapter();
};
// Persist last values across component remounts (page switches).
let marketBannerDataCache = {};

const MarketBannerItem = memo(({ index, data, formatNumber }) => {
  const hasLiveData = Boolean(data);
  const isNegative = (data?.change || 0) < 0;

  return (
    <div className="market-banner-item">
      <span className="market-banner-group">
        <span className="market-banner-name">{data?.name || index.name}</span>
        <span className="market-banner-value">
          {hasLiveData ? formatNumber(data?.lastRate) : "-"}
        </span>
        {hasLiveData ? (
          <>
            <span
              className={`market-banner-change ${isNegative ? "negative" : "positive"}`}
            >
              {isNegative ? "-" : "+"}
              {Math.abs(data.change).toFixed(2)} (
              {Math.abs(data.percentChange).toFixed(2)}%)
            </span>
            <span className={isNegative ? "arrow-down" : "arrow-up"}>
              {isNegative ? "▼" : "▲"}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if key values have changed significantly
  const prevData = prevProps.data;
  const nextData = nextProps.data;

  if (!prevData && !nextData) return true;
  if (!prevData || !nextData) return false;

  // Check if values have changed by more than 0.01 to avoid micro-updates
  const lastRateChanged = Math.abs((prevData.lastRate || 0) - (nextData.lastRate || 0)) > 0.01;
  const changeChanged = Math.abs((prevData.change || 0) - (nextData.change || 0)) > 0.01;
  const percentChanged = Math.abs((prevData.percentChange || 0) - (nextData.percentChange || 0)) > 0.01;

  return !lastRateChanged && !changeChanged && !percentChanged;
});

const MarketBannerScrollContent = memo(({ indices, selected, indicesData, formatNumber }) => {
  return (
    <>
      {/* First set of indices */}
      <div className="market-banner-content">
        {indices
          .filter((i) => selected.includes(i.scripCode))
          .map((index) => (
            <MarketBannerItem
              key={index.scripCode}
              index={index}
              data={indicesData[index.scripCode]}
              formatNumber={formatNumber}
            />
          ))}
      </div>

      {/* Duplicate set for seamless loop */}
      <div className="market-banner-content">
        {indices
          .filter((i) => selected.includes(i.scripCode))
          .map((index) => (
            <MarketBannerItem
              key={`${index.scripCode}-duplicate`}
              index={index}
              data={indicesData[index.scripCode]}
              formatNumber={formatNumber}
            />
          ))}
      </div>
    </>
  );
}, (prevProps, nextProps) => {
  // Only re-render if indices or selected change, not if data values change
  return (
    prevProps.indices === nextProps.indices &&
    prevProps.selected === nextProps.selected &&
    prevProps.formatNumber === nextProps.formatNumber
  );
});

const MarketBanner = () => {
  const wsPriceUnsubRef = useRef(null);
  const angelWsPriceUnsubRef = useRef(null);
  const angelIndicesSubscriptionsRef = useRef([]);
  const marqueeContentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [indicesData, setIndicesData] = useState(() => marketBannerDataCache);
  const lastValidDataRef = useRef(marketBannerDataCache);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null); // Ref for dropdown container
  const activeBrokerFlag = toPmsBrokerFlag(
    localStorage.getItem("broker_type") || "5paisa",
  );
  const isGrowBroker = activeBrokerFlag === "grow";
  const isAngelOneBroker = activeBrokerFlag === "angelone";
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";

  const [indices, setIndices] = useState([]);

  // const indices = [
  //   { name: "NIFTY", scripCode: "999920000", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTY" },
  //   { name: "SENSEX", scripCode: "999901", Exch: "B", ExchType: "C", growSymbol: "BSE_SENSEX" },
  //   { name: "BANKNIFTY", scripCode: "999920005", Exch: "N", ExchType: "C", growSymbol: "NSE_BANKNIFTY" },
  //   { name: "FINNIFTY", scripCode: "999920041", Exch: "N", ExchType: "C", growSymbol: "NSE_FINNIFTY" },
  //   { name: "NIFTY MIDCAP 100", scripCode: "999920015", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYMIDCAP" },
  //   { name: "NIFTY SMALLCAP 100", scripCode: "999920027", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYSMALL" },
  //   { name: "NIFTY IT", scripCode: "999920009", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYIT" },
  //   { name: "NIFTY AUTO", scripCode: "999920010", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYAUTO" },
  //   { name: "NIFTY PHARMA", scripCode: "999920011", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYPHARMA" },
  //   { name: "NIFTY FMCG", scripCode: "999920012", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYFMCG" },
  //   { name: "NIFTY METAL", scripCode: "999920013", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYMETAL" },
  //   { name: "NIFTY ENERGY", scripCode: "999920014", Exch: "N", ExchType: "C", growSymbol: "NSE_NIFTYENERGY" }
  // ];


  useEffect(() => {
    const fetchIndices = async () => {
      try {
        const user_id =
          Number(localStorage.getItem("id")) || 0;

        const email =
          localStorage.getItem("email") || "";

        const flag = toPmsBrokerFlag(
          localStorage.getItem("broker_type") || "5paisa",
        );

        const res = await fetch(
          `${pmsApiBaseUrl}/api/v1/dashboard/top_market_indices`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id,
              email,
              flag,
            }),
          }
        );

        const json = await res.json();
        const apiData = json?.data || {};
        const flagFromApi = toPmsBrokerFlag(apiData?.flag || flag);
        const adapter = getMarketIndicesAdapter(flagFromApi);
        const list = adapter.extractList(apiData);
        const mapped = adapter.normalizeIndices(list);

        setIndices(mapped);
      } catch (err) {
        console.error("Error fetching indices:", err);
      }
    };

    fetchIndices();
  }, []);




  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    const number = parseFloat(num);
    if (isNaN(number)) return "-";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Default: first 4 indices selected
  // const [selected, setSelected] = useState(indices.slice(0, 6).map(i => i.scripCode));
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!indices.length) return;

    setSelected(indices.slice(0, 6).map((i) => i.scripCode));
  }, [indices]);




  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") return undefined;

    const indexByCode = new Map(indices.map((i) => [i.scripCode, i]));
    wsPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((stock) => {
      const token = String(stock?.Token || "");
      const indexInfo = indexByCode.get(token);
      if (!indexInfo) return;

      const { LastRate, PClose, High, Low } = stock;
      if (LastRate && PClose && PClose !== 0) {
        const change = LastRate - PClose;
        const percentChange = (change / PClose) * 100;
        lastValidDataRef.current[token] = {
          name: indexInfo.name,
          lastRate: LastRate,
          PClose,
          high: High,
          low: Low,
          change,
          percentChange,
        };
      }

      setIndicesData((prev) => {
        const next = {
          ...prev,
          [token]: lastValidDataRef.current[token],
        };
        marketBannerDataCache = next;
        return next;
      });
    });

    fivePaisaWebSocketService.connect();
    fivePaisaWebSocketService.subscribeMarketFeed(
      indices.map((i) => ({
        Exch: i.Exch,
        ExchType: i.ExchType,
        ScripCode: Number(i.scripCode),
      })),
    );

    return () => {
      wsPriceUnsubRef.current?.();
      fivePaisaWebSocketService.unsubscribeMarketFeed(
        indices.map((i) => ({
          Exch: i.Exch,
          ExchType: i.ExchType,
          ScripCode: Number(i.scripCode),
        })),
      );
      fivePaisaWebSocketService.disconnect({ manual: false });
    };
  }, [activeBrokerFlag, indices]);

  useEffect(() => {
    if (!isAngelOneBroker) {
      if (angelIndicesSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelIndicesSubscriptionsRef.current,
        );
        angelIndicesSubscriptionsRef.current = [];
      }
      return undefined;
    }

    const indexByCode = new Map(indices.map((i) => [String(i.scripCode), i]));

    angelWsPriceUnsubRef.current = angelOneWebSocketService.onPrice((stock) => {
      const token = String(stock?.Token || "");
      const indexInfo = indexByCode.get(token);
      if (!indexInfo) return;

      const { LastRate, PClose, High, Low } = stock;
      if (LastRate && PClose && PClose !== 0) {
        const change = LastRate - PClose;
        const percentChange = (change / PClose) * 100;
        lastValidDataRef.current[token] = {
          name: indexInfo.name,
          lastRate: LastRate,
          PClose,
          high: High,
          low: Low,
          change,
          percentChange,
        };
      }

      setIndicesData((prev) => {
        const next = {
          ...prev,
          [token]: lastValidDataRef.current[token],
        };
        marketBannerDataCache = next;
        return next;
      });
    });

    const feedItems = indices
      .map((index) => {
        const token = String(index?.token || index?.scripCode || "").trim();
        if (!token) return null;
        const exchangeType = getAngelExchangeTypeFromHolding(index);
        if (!exchangeType) return null;
        return { exchangeType, token };
      })
      .filter(Boolean);

    if (feedItems.length) {
      angelOneWebSocketService.connect();
      for (let i = 0; i < feedItems.length; i += 50) {
        angelOneWebSocketService.subscribeMarketFeed(feedItems.slice(i, i + 50));
      }
      angelIndicesSubscriptionsRef.current = feedItems;
    }

    return () => {
      angelWsPriceUnsubRef.current?.();
      angelWsPriceUnsubRef.current = null;
      if (angelIndicesSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelIndicesSubscriptionsRef.current,
        );
        angelIndicesSubscriptionsRef.current = [];
      }
    };
  }, [isAngelOneBroker, indices]);

  useEffect(() => {
    if (!isGrowBroker) {
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
      return undefined;
    }

    const indexByGrowSymbol = new Map(
      indices.map((idx) => [idx.growSymbol, idx]),
    );

    const symbols = Array.from(
      new Set(indices.map((idx) => idx.growSymbol).filter(Boolean)),
    );

    if (symbols.length === 0) {
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
      return undefined;
    }

    growwLivePriceService.setSymbols(GROW_POLL_SOURCE, symbols);
    const unsubscribe = growwLivePriceService.subscribe((payload) => {
      if (!payload || typeof payload !== "object") return;

      setIndicesData((prev) => {
        const next = { ...prev };

        symbols.forEach((symbol) => {
          const indexInfo = indexByGrowSymbol.get(symbol);
          if (!indexInfo) return;

          const normalizedSymbol = String(symbol).replace(/[:\s]+/g, "_");
          const raw =
            payload?.[symbol] ??
            payload?.[normalizedSymbol] ??
            null;

          const lastRate = Number(
            raw?.ltp ??
            raw?.last_price ??
            raw?.lastPrice ??
            raw?.lastRate ??
            raw?.price ??
            raw?.close ??
            raw,
          );
          // let previousClose = Number(
          //   raw?.previous_close ??
          //   raw?.close ??
          //   raw?.pClose ??
          //   raw?.PClose ??
          //   raw?.prev_close ??
          //   raw?.prevClose,
          // );
          let previousClose = Number(
            raw?.previous_close ??
            raw?.close ??
            raw?.pClose ??
            raw?.PClose ??
            raw?.prev_close ??
            raw?.prevClose ??
            indexInfo?.prevClose // ✅ fallback from API
          );
          if (!Number.isFinite(previousClose) || previousClose === 0) {
            previousClose = lastRate; // fallback to avoid NaN
          }
          const dayChange = Number(raw?.day_change ?? raw?.change);
          const dayChangePerc = Number(raw?.day_change_perc ?? raw?.day_change_percent ?? raw?.change_perc);
          const high = Number(raw?.high);
          const low = Number(raw?.low);

          if (!Number.isFinite(lastRate)) {
            return;
          }

          let change = 0;
          let percentChange = 0;
          if (Number.isFinite(previousClose) && previousClose !== 0) {
            change = lastRate - previousClose;
            percentChange = (change / previousClose) * 100;
          } else if (Number.isFinite(dayChangePerc)) {
            percentChange = dayChangePerc;
            if (Number.isFinite(dayChange)) {
              change = dayChange;
            } else {
              change = (lastRate * percentChange) / 100;
            }
            previousClose = lastRate - change;
          } else if (Number.isFinite(dayChange)) {
            change = dayChange;
            previousClose = lastRate - change;
            percentChange =
              Number.isFinite(previousClose) && previousClose !== 0
                ? (change / previousClose) * 100
                : 0;
          } else {
            previousClose = previousClose || lastRate;
          }

          next[indexInfo.scripCode] = {
            name: indexInfo.name,
            lastRate,
            PClose: previousClose,
            high: Number.isFinite(high) ? high : 0,
            low: Number.isFinite(low) ? low : 0,
            change,
            percentChange,
          };
          lastValidDataRef.current[indexInfo.scripCode] = next[indexInfo.scripCode];
        });

        marketBannerDataCache = next;
        return next;
      });
    });

    growwLivePriceService.startPolling(2000);

    return () => {
      unsubscribe?.();
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
    };
  }, [isGrowBroker, indices]);

  // Toggle logic - no max limit, but minimum 1
  const toggleIndex = (code) => {
    setSelected((prev) => {
      if (prev.includes(code)) {
        // Prevent going below 1
        if (prev.length <= 1) return prev;
        return prev.filter((c) => c !== code);
      } else {
        // No max limit - allow selecting all
        return [...prev, code];
      }
    });
  };







  if (!indices.length) return;

  return (
    <div className="market-banner-container" style={{ backgroundColor: "#EFF4FB" }}>
      {/* Left fixed dropdown */}
      {activeBrokerFlag !== "grow" && <div className="market-banner-left">
        <div
          className="dropdown"
          ref={dropdownRef} // Attach ref to dropdown container
        >
          {/* Professional Toggle Button */}
          <button
            className=""
            style={{
              padding: "8px 10px",
              height: "auto",
              // color: "white", 
              // background: "linear-gradient(135deg, rgba(36, 87, 191, 0.9) 0%, rgba(26, 66, 160, 0.9) 100%)",
              fontSize: "20px",
              borderRadius: "8px",
              color: "#2457BF",
              // border: "none",
              backgroundColor: "#FFF",
              border: "1px solid #edebeb",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
            }}
          >
            <IoIosArrowDropdownCircle />
          </button>

          {/* Professional Dropdown Content */}
          {isDropdownOpen && (
            <div
              className="dropdown-content"
              style={{
                display: "flex",
                flexDirection: "column",
                width: "280px",
                borderRadius: '12px',
                fontSize: "13px",
                color: "#0f172a",
                backgroundColor: "#ffffff",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                marginTop: "8px",
                position: "absolute",
                zIndex: 1000
              }}
            >
              {/* Header */}
              <div style={{
                padding: "12px 14px",
                background: "linear-gradient(135deg, #F2F7FD 0%, #e8f1fc 100%)",
                borderBottom: "1px solid #e2e8f0",
                fontWeight: "700",
                fontSize: "12px",
                color: "#0f172a",
                letterSpacing: "-0.2px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Select Indices</span>
                <span style={{
                  padding: "2px 8px",
                  backgroundColor: "#f0f9ff",
                  color: "#2457BF",
                  borderRadius: "5px",
                  fontSize: "10px",
                  fontWeight: "700"
                }}>
                  {selected.length}/{indices.length}
                </span>
              </div>

              {/* Checkbox List */}
              <div style={{
                maxHeight: "320px",
                overflowY: "auto",
                scrollbarWidth: "thin"
              }}>
                {indices.map((index) => {
                  const isChecked = selected.includes(index.scripCode);
                  const disableUncheck = selected.length <= 1 && isChecked;
                  const isDisabled = disableUncheck;

                  return (
                    <label
                      key={index.scripCode}
                      className={`market-banner-dropdown-item ${isChecked ? "checked" : ""} ${isDisabled ? "disabled" : ""}`}
                      onMouseEnter={(e) => {
                        if (!isDisabled) {
                          e.currentTarget.style.backgroundColor = "#F2F7FD";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isChecked ? "#f8fafc" : "#ffffff";
                      }}
                    >
                      <div className="market-banner-item-content">
                        <div className="market-banner-checkbox-wrap">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => toggleIndex(index.scripCode)}
                            className="market-banner-checkbox"
                          />
                        </div>

                        <span className="market-banner-dropdown-text">
                          {index.name}
                        </span>

                        {isChecked && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="market-banner-status-icon"
                          >
                            <circle cx="8" cy="8" r="8" fill="#1EA355" />
                            <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>

                      {disableUncheck && (
                        <div className="market-banner-disabled-hint">
                          At least 1 required
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Footer Info - Dynamic */}
              <div style={{
                padding: "10px 14px",
                background: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
                fontSize: "11px",
                color: "#64748b",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}>
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="#64748b" strokeWidth="1.5" />
                  <path d="M7 4.5V7.5M7 9.5H7.005" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Select {selected.length > 0 ? "more " : ""}indices
              </div>
            </div>
          )}
        </div>
      </div>}

      {/* Right side evenly spaced stocks */}
      <div className="market-banner-right">
        <div
          className="market-banner-scroll"
          ref={scrollContainerRef}
        >
          <MarketBannerScrollContent
            indices={indices}
            selected={selected}
            indicesData={indicesData}
            formatNumber={formatNumber}
          />
        </div>
      </div>
    </div>
  );
};

export default MarketBanner;
